import React, { useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { token, useThemeObserver } from '@atlaskit/tokens';

import { ScreenEvent } from '@confluence/analytics/entry-points/ScreenEvent.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { CrossIconCloseButton } from './CrossIconCloseButton.tsx';
import GetTheDetailsYouNeedDark from './assets/GetTheDetailsYouNeedDark.gif';
import GetTheDetailsYouNeedLight from './assets/GetTheDetailsYouNeedLight.gif';
import { useMoreActionsObjectSidebarTourState } from './useMoreActionsObjectSidebarTourState.ts';
import { OBJECT_SIDEBAR_TOUR_TARGETS } from './objectSidebarTourTargets.ts';
import { MessageType } from './objectSidebarTourMessageTypes.ts';

export enum MoreActionsObjectSidebarTourSteps {
	FIRST,
}

const i18n = defineMessages({
	ok: {
		id: 'object-sidebar-components.more-actions.tour.ok',
		defaultMessage: 'OK',
		description: 'A label that indicates which button should be clicked to end the tour',
	},
	getTheDetailsYouNeed: {
		id: 'object-sidebar-components.more-actions.tour.get-the-details-you-need',
		defaultMessage: 'Get the details you need',
		description:
			'The heading of a spotlight highlighting the benefits of the Details Panel, which includes information about the content it appears alongside',
	},
	viewAndManageOwnership: {
		id: 'object-sidebar-components.more-actions.tour.view-and-manage-ownership',
		defaultMessage: 'View and manage ownership, labels, and more in a single panel.',
		description:
			'The body of a spotlight highlighting the benefits of the Details Panel, which includes information about ownership, labels, and other content',
	},
});

const MORE_ACTIONS_OBJECT_SIDEBAR_SPOTLIGHT_NAME = 'more-actions-object-sidebar-spotlight';

type UseSpotlightsProps = {
	end: () => void;
};

const useSpotlights = ({ end }: UseSpotlightsProps) => {
	const { formatMessage } = useIntl();
	const theme = useThemeObserver();
	const GetTheDetailsYouNeedGif =
		theme.colorMode === 'dark' ? GetTheDetailsYouNeedDark : GetTheDetailsYouNeedLight;

	return [
		{
			key: OBJECT_SIDEBAR_TOUR_TARGETS.SIDEBAR_DETAILS_BUTTON,
			target: OBJECT_SIDEBAR_TOUR_TARGETS.SIDEBAR_DETAILS_BUTTON,
			targetRadius: 3,
			actions: [{ onClick: end, text: formatMessage(i18n.ok) }],
			image: GetTheDetailsYouNeedGif,
			dialogWidth: 341,
			heading: formatMessage(i18n.getTheDetailsYouNeed),
			headingAfterElement: (
				<CrossIconCloseButton onClose={end} source={MORE_ACTIONS_OBJECT_SIDEBAR_SPOTLIGHT_NAME} />
			),
			children: formatMessage(i18n.viewAndManageOwnership),
		},
	];
};

type UseMoreActionsTourProps = {
	step: number;
};

const FLOATING_ACTION_BAR_CHANGEBOARDING = 'floatingActionBarChangeboarding';
const SCREEN_EVENT_SUBJECTS = [FLOATING_ACTION_BAR_CHANGEBOARDING];

export const useMoreActionsObjectSidebarTour = ({
	step,
}: UseMoreActionsTourProps): {
	activeSpotlight: React.JSX.Element | null;
	initiateTour: () => void;
	concludeTour: () => void;
	resetTour: () => void;
	activeSpotlightIndex: number | null;
	lastActiveSpotlightIndex: number | null;
} => {
	const [
		{ activeSpotlightIndex, lastActiveSpotlightIndex },
		{ concludeTour: endTour, initiateTour, resetTour },
	] = useMoreActionsObjectSidebarTourState();

	const [messageType, _] = useState<MessageType>(MessageType.Transactional);

	const { Spotlight } = useChoreographerAssets();

	const concludeTour = useCallback((): void => {
		endTour();
	}, [endTour]);

	const spotlights = useSpotlights({
		end: concludeTour,
	});

	return {
		activeSpotlight:
			activeSpotlightIndex === step ? (
				<>
					<Spotlight
						messageType={messageType}
						shouldWatchTarget
						dialogPlacement="left bottom"
						targetBgColor={token('elevation.surface.overlay')}
						{...spotlights[activeSpotlightIndex]}
					/>
					<ScreenEvent
						id={SCREEN_EVENT_SUBJECTS[activeSpotlightIndex]}
						name={SCREEN_EVENT_SUBJECTS[activeSpotlightIndex]}
					/>
				</>
			) : null,
		initiateTour,
		concludeTour,
		resetTour,
		activeSpotlightIndex,
		lastActiveSpotlightIndex,
	};
};
